import { SURVEY_TEMPLATE_TYPES } from "constants/global";
import { APIData } from "types/cms/frontendTypes";
import { API_METHOD } from "types/common";

export interface AnswerChoice {
  title: string;           // Required, maxLength 50
  value: string;           // Required
  order: number;
  readMapper?: string;
  writeMapper?: string;
  unselectMapper?: string;
  subChoices?: SubChoice[];
}

export interface SubChoice {
  title: string;
  value: string;
  order: number;
  selectionType: 'NONE' | 'LIST';
  subChoiceValidationType: 'NONE' | 'ASC_ALLOWED_VALUES';
  allowedValues?: Record<string, any>; // JSON field
}

export type CreateSurveyQuestionRequestData = Omit<APIData<'api::survey-question.survey-question'>, 
  'documentId' | 'createdAt' | 'updatedAt' | 'publishedAt' | 'questionId' | 'utility'> & 
  { localizations: SurveyQuestionLocalization[] }

export interface SurveyQuestionLocalization {
  locale : string;
  title : string;
  info ?: string
  choices : LocalisedAnswerChoice[]
}

export type LocalisedAnswerChoice = Omit<AnswerChoice, 
    'order' | 'subChoices' | 'readMapper' | 'writeMapper' | 'unselectMapper' > & 
    { subChoices?: LocalisedSubChoice[]}

export type LocalisedSubChoice = Omit<SubChoice, 'order'>
  
export type SurveyChoices = Pick<APIData<'api::survey-question.survey-question'>, 'choices'>['choices']

export interface SurveyTemplateLocalization {
  locale : string
  templateQuestions : SurveyTemplateQuestion[]
}

export interface SurveyTemplateQuestion {
  questionUid : string
  title : string
}

export namespace GetQuestionBank {

  export const method: API_METHOD = "GET";

  export const apiEndpoint = '/survey/getQuestionBank'

  export type retval = {
    data : {
      surveyCategories : APIData<'api::survey-category.survey-category'>[]
      surveyQuestions : {
        data : {
          draft : APIData<"api::survey-question.survey-question">[]
          published : APIData<"api::survey-question.survey-question">[]
          modified : APIData<"api::survey-question.survey-question">[]
        }
        meta : {
          count : {
            draft : number
            published : number
            modified : number
          }
        }
      }
    }
  }

  export type params = { 
  }
}

export namespace UpdateQuestions {

  export const method: API_METHOD = "POST";

  export const apiEndpoint = '/survey/updateQuestions'

  export type params = {
    utilityDocumentId : string
    pilotId : string
    data : Partial<(APIData<'api::survey-question.survey-question'> & { localizations : SurveyQuestionLocalization[] })>[]
  }

  export type retval = {
    error ?: Record<string, string>[]
    questions : APIData<'api::survey-question.survey-question'>[]
  }
}


export namespace CreateSurveyTemplate {

  export const method: API_METHOD = "POST";

  export const apiEndpoint = '/survey/createTemplate'

  export type retval = APIData<'api::survey-template.survey-template'>

  export type params = {
    templateType : (typeof SURVEY_TEMPLATE_TYPES)[number]
    label : string
  }
}

export namespace GetSurveyTemplates {

  export const method: API_METHOD = "GET";

  export const apiEndpoint = '/survey/getTemplates'

  export type retval = {
    data : {
      draft : APIData<'api::survey-template.survey-template'>[]
      modified : APIData<'api::survey-template.survey-template'>[]
      published : APIData<'api::survey-template.survey-template'>[]
    }
  }

  export type params = {
  }
}

export namespace UpdateAllTemplates {

  export const method: API_METHOD = "POST";

  export const apiEndpoint = '/survey/updateAllTemplates'

  export type retval = {
    templates : APIData<'api::survey-template.survey-template'>[],
    error : Record<string, string>[]
  }

  export type params = {
    utilityDocumentId : string
    data : Partial<(APIData<'api::survey-template.survey-template'> & { localizations : SurveyTemplateLocalization[] })>[]
  }
}

export namespace PublishTemplates {

  export const method: API_METHOD = "POST";

  export const apiEndpoint = '/survey/publishTemplates'

  export type retval = APIData<'api::survey-template.survey-template'>[]

  export type params = {
    data : string[]
  }
}

export namespace UnpublishTemplates {

  export const method: API_METHOD = "POST";

  export const apiEndpoint = '/survey/unpublishTemplates'

  export type retval = APIData<'api::survey-template.survey-template'>[]

  export type params = {
    data : string[]
  }
}

export namespace DiscardAllDraftTemplates {

  export const method: API_METHOD = "POST";

  export const apiEndpoint = '/survey/discardAllDraftTemplates'

  export type retval = string[]

  export type params = {
    data : string[]
  }
}